import React from "react";
import AgentSelect from "./AgentSelect";

// Friendly operator labels shown in the editor.
const OP_LABEL = {
  eq: "is", ne: "is not", contains: "contains", not_contains: "doesn't contain",
  starts_with: "starts with", in: "is any of", not_in: "is none of",
  gt: "is greater than", gte: "is at least", lt: "is less than", lte: "is at most",
  exists: "is set", not_exists: "is empty", is_true: "is yes", is_false: "is no"
};

const NO_VALUE = ["exists", "not_exists", "is_true", "is_false"];
const MULTI = ["in", "not_in"];

/**
 * Edits a condition: { join: "all"|"any", rules: [{ field, op, value }] }.
 * `fields` is the vocabulary from /agents/condition-fields.
 */
const ConditionEditor = ({ value, onChange, fields }) => {
  const cond = value && Array.isArray(value.rules) ? value : { join: "all", rules: [] };
  const defs = fields || [];
  const byKey = {};
  defs.forEach(f => { byKey[f.key] = f; });

  const commit = next => onChange(next);
  const setJoin = j => commit({ ...cond, join: j });
  const setRule = (i, patch) => commit({ ...cond, rules: cond.rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });
  const removeRule = i => commit({ ...cond, rules: cond.rules.filter((_, idx) => idx !== i) });
  const addRule = () => {
    const f = defs[0];
    commit({ ...cond, rules: [...cond.rules, { field: f ? f.key : "", op: f ? (f.operators || ["eq"])[0] : "eq", value: "" }] });
  };

  // When the field changes, reset op to the field's first valid operator and clear the value.
  const onFieldChange = (i, key) => {
    const f = byKey[key];
    setRule(i, { field: key, op: f ? (f.operators || ["eq"])[0] : "eq", value: "" });
  };

  const fieldOptions = defs.map(f => ({ value: f.key, label: f.group ? `${f.group} · ${f.label}` : f.label }));

  const renderValue = (rule, i) => {
    if (NO_VALUE.includes(rule.op)) return null;
    const f = byKey[rule.field] || {};
    if (MULTI.includes(rule.op)) {
      const shown = Array.isArray(rule.value) ? rule.value.join(", ") : (rule.value || "");
      return (
        <input className="ag-input ag-cond__val" placeholder="comma-separated values"
          value={shown}
          onChange={e => setRule(i, { value: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} />
      );
    }
    if (f.type === "enum" && Array.isArray(f.options) && f.options.length) {
      return (
        <div className="ag-cond__val">
          <AgentSelect searchable placeholder="Select…" value={rule.value || ""}
            onChange={v => setRule(i, { value: v })}
            options={f.options.map(o => ({ value: o, label: o }))} />
        </div>
      );
    }
    return (
      <input className="ag-input ag-cond__val" type={f.type === "number" ? "number" : "text"}
        placeholder="value" value={rule.value == null ? "" : rule.value}
        onChange={e => setRule(i, { value: e.target.value })} />
    );
  };

  return (
    <div className="ag-cond">
      {cond.rules.length > 1 && (
        <div className="ag-cond__join">
          <span>Match</span>
          <button type="button" className={`ag-cond__jbtn ${cond.join !== "any" ? "is-on" : ""}`} onClick={() => setJoin("all")}>ALL</button>
          <button type="button" className={`ag-cond__jbtn ${cond.join === "any" ? "is-on" : ""}`} onClick={() => setJoin("any")}>ANY</button>
          <span>of these</span>
        </div>
      )}
      {cond.rules.map((rule, i) => {
        const f = byKey[rule.field] || {};
        const ops = f.operators || ["eq", "ne"];
        return (
          <div className="ag-cond__row" key={i}>
            <div className="ag-cond__field">
              <AgentSelect searchable value={rule.field} onChange={v => onFieldChange(i, v)} options={fieldOptions} />
            </div>
            <div className="ag-cond__op">
              <AgentSelect value={rule.op} onChange={v => setRule(i, { value: NO_VALUE.includes(v) ? "" : rule.value, op: v })}
                options={ops.map(o => ({ value: o, label: OP_LABEL[o] || o }))} />
            </div>
            {renderValue(rule, i)}
            <button type="button" className="ag-cond__del material-icons-outlined" title="Remove" onClick={() => removeRule(i)}>close</button>
          </div>
        );
      })}
      <button type="button" className="ag-cond__add" onClick={addRule}>+ Add condition</button>
    </div>
  );
};

export default ConditionEditor;
